/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card } from 'primereact/card';
import { Dropdown } from "primereact/dropdown";
import { useEffect, useRef, useState } from "react";
import {useDispatch, useSelector} from "react-redux";
import LayoutWrapper from "./LayoutWrapper";
import Highcharts from "highcharts";
import {TabPanel, TabView} from "primereact/tabview";
import {pushMessage} from "../reducers/messagingReducer";

/**
 * @typedef {Object} Datapoint
 * @property {number} Datapoint.domain_id
 * @property {number} Datapoint.marketplace_id
 * @property {number} Datapoint.impression
 * @property {number} Datapoint.revenue
 * @property {number} Datapoint.ecpm
 * @property {number} Datapoint.winrates
 * @property {string} Datapoint.date
 */

/**
 * @typedef {Object} AggregatedData
 * @property {number} AggregatedData.impression
 * @property {string} AggregatedData.impression_change
 * @property {number} AggregatedData.revenue
 * @property {string} AggregatedData.revenue_change
 * @property {number} AggregatedData.ecpm
 * @property {string} AggregatedData.ecpm_change
 * @property {number} AggregatedData.winrates
 * @property {string} AggregatedData.winrates_change
 */

/**
 * @typedef {Object} ReportData
 * @property {AggregatedData} ReportData.aggregationData
 * @property {Datapoint[]} ReportData.data
 */

const SSP_REPORT_DATA_URL = process.env.REACT_APP_SSP_REPORTING_BASE_URL

const MONTH_RANGE_OPTIONS = [
    { name: 'Month-to-date', value: 'month-to-day' },
    { name: 'Last month', value: 'last-month' },
    { name: 'Year-to-date', value: 'year-to-date' }
]

const FORMAT_OPTIONS = [
    {name: 'Display', value: 'banner'},
    {name: 'Video', value: 'video'}
]

const dashboardControlStyles = css`
    display: flex;
    .filter {
        display: flex;
        flex-direction: column;
        margin-right: 2em;
        .label {
            margin-bottom: 0.25em;
            font-weight: 600;
        }
    }
`

const reportingSectionStyle = css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    padding: 20px 0;
    .v {
        text-align: center;
        font-weight: bold;
    }
    .info {
        display: flex;
        align-items: center;
        justify-content: center;
        span {
            padding: 0 0.25em;
        }
        &.down {
            svg {
                color: #ff0000;
                transform: rotate(180deg);
            }
            .n {
                color: #ff0000;
            }
        }
        &.up {
            svg {
                color: #409d40;
            }
            .n {
                color: #409d40;
            }
        }
    }
`

const MONTH_ABBRV = {
    '01': 'Jan',
    '02': 'Feb',
    '03': 'Mar',
    '04': 'Apr',
    '05': 'May',
    '06': 'Jun',
    '07': 'Jul',
    '08': 'Aug',
    '09': 'Sept',
    '10': 'Oct',
    '11': 'Nov',
    '12': 'Dec'
}

/**
 * @param {Object} config
 * @param {string} config.chartTitle
 * @param {string} config.leftYAxisUnit
 * @param {string} config.rightYAxisUnit
 * @param {string} config.leftYAxisName
 * @param {string} config.rightYAxisName
 * @param {string[]} config.xAxisLabels
 * @param {string} config.columnName
 * @param {string} config.lineName
 * @param {string} config.columnValuePrefix
 * @param {string} config.lineValuePrefix
 * @param {number[]} config.columnValues
 * @param {number[]} config.lineValues
 * @param {React.RefObject<HTMLDivElement>} config.ref
 */

function createDualChart(config) {
    if (config.ref.current) {
        Highcharts.chart(config.ref.current.id, {
            chart: {
                zooming: {
                    type: 'xy'
                }
            },
            title: {
                text: config.chartTitle,
                align: 'center'
            },
            xAxis: [{
                categories: config.xAxisLabels,
                crosshair: false
            }],
            yAxis: [
                {
                    title: {
                        text: config.rightYAxisName,
                        style: {
                            color: "#666"
                        }
                    },
                    labels: {
                        format: '{value} ' + config.rightYAxisUnit,
                        style: {
                            color: "#666"
                        }
                    },
                    opposite: true
                }, {
                    labels: {
                        format: '{value} ' + config.leftYAxisUnit,
                        style: {
                            color: "rgb(64, 157, 64)"
                        }
                    },
                    title: {
                        text: config.leftYAxisName,
                        style: {
                            color: "rgb(64, 157, 64)"
                        }
                    }
                }],
            tooltip: {
                shared: true
            },
            series: [{
                name: config.lineName,
                type: 'spline',
                color: "#666",
                data: config.lineValues,
                dashStyle: 'LongDash',
                tooltip: {
                    valuePrefix: config.lineValuePrefix,
                }
            }, {
                name: config.columnName,
                type: 'column',
                color: 'rgb(64, 157, 64)',
                color: 'transparent',
                borderColor: 'rgb(64, 157, 64)',
                borderWidth: 2,
                yAxis: 1,
                data: config.columnValues,
                tooltip: {
                    valuePrefix: config.columnValuePrefix
                }
            },]
        })
    }
}

export function SSPDashboard() {
    const { currentDomain, currentCustomer, adapterReportUrl } = useSelector(state => state.userInformation)
    const dispatch = useDispatch();
    /** @type {[ReportData, React.Dispatch<React.SetStateAction<ReportData>>]} */
    const [reportingData, setReportingData] = useState(null)
    const [dateRange, setDateRange] = useState(null)
    /** @type {React.RefObject<HTMLDivElement>} */
    const revenueChartRef = useRef(null)
    /** @type {React.RefObject<HTMLDivElement>} */
    const winRateChartRef = useRef(null)
    /** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
    const [format, setFormat] = useState('banner')

    useEffect(() => {
        if (currentDomain && currentCustomer) {
            let d = dateRange || MONTH_RANGE_OPTIONS[0].value
            let requestUrl = SSP_REPORT_DATA_URL + currentDomain + "/" + d + "/" + format

            fetch(requestUrl)
                .then(data => {
                    return data.json()
                })
                .then(data => {
                    /** @type {ReportData} */
                    let reportData = data
                    reportData.data = reportData
                        .data
                        .sort((a, b) => {
                            return a.date.localeCompare(b.date)
                        })
                    setReportingData(reportData)
                })
                .catch(err => {
                    console.log({ err })
                })
        }
    }, [currentDomain, currentCustomer, dateRange, format])

    useEffect(() => {
        if (reportingData) {

            createDualChart({
                chartTitle: 'Revenue & Impressions',
                columnName: 'Revenue',
                columnValuePrefix: '$ ',
                columnValues: reportingData.data.map(item => {
                    return item.revenue
                }),
                leftYAxisName: 'Revenue',
                leftYAxisUnit: '$',
                lineName: 'Impressions',
                lineValuePrefix: '&#128065; ',
                lineValues: reportingData.data.map(item => {
                    return item.impression
                }),
                rightYAxisName: 'Impressions',
                rightYAxisUnit: 'imp',
                xAxisLabels: reportingData.data.map(item => {
                    let [, mm, dd] = item.date.split('-')
                    return MONTH_ABBRV[mm] + ' ' + dd
                }),
                ref: revenueChartRef
            })
            createDualChart({
                chartTitle: 'Win rate & eCPM',
                columnName: 'eCPM',
                columnValuePrefix: 'CPM ',
                columnValues: reportingData.data.map(item => {
                    return item.ecpm
                }),
                leftYAxisName: 'eCPM',
                leftYAxisUnit: '$',
                lineName: 'Win rate',
                lineValuePrefix: '% ',
                lineValues: reportingData.data.map(item => {
                    return item.winrates
                }),
                rightYAxisName: 'Win Rate',
                rightYAxisUnit: '%',
                xAxisLabels: reportingData.data.map(item => {
                    let [, mm, dd] = item.date.split('-')
                    return MONTH_ABBRV[mm] + ' ' + dd
                }),
                ref: winRateChartRef
            })
        }
    }, [revenueChartRef, winRateChartRef, reportingData])

    return (
        <LayoutWrapper
            model="adapter"
            renderOpsBar={true}

        >
            <TabView onTabChange={(e) => {
                if (e.index === 1) {
                    if (adapterReportUrl) {
                        window.open(adapterReportUrl, '_blank');
                    } else {
                        dispatch(pushMessage("Adapter Report", "Adapter Report URL not found ! \n Please save the URL for report first", "error"));
                    }
                }
            }}>
                <TabPanel header="Dashboard" activeIndex={0} >
                    <section id="reporting-dashboard">

                        <div css={dashboardControlStyles}>
                            <div className="filter">
                                <span className="label">Date Range</span>
                                <Dropdown
                                    optionLabel="name"
                                    optionValue="value"
                                    value={dateRange || MONTH_RANGE_OPTIONS[0].value}
                                    options={MONTH_RANGE_OPTIONS}
                                    onChange={v => {
                                        setDateRange(v.value)
                                    }}
                                />
                            </div>
                            <div className="filter">
                                <span className="label">Format</span>
                                <Dropdown
                                    optionLabel="name"
                                    optionValue="value"
                                    value={format}
                                    options={FORMAT_OPTIONS}
                                    onChange={v => {
                                        setFormat(v.value)
                                    }}
                                />
                            </div>
                        </div>
                        {
                            reportingData && (
                                <>
                                    {
                                        Object.values(reportingData.aggregationData).length
                                            ? (<section className="data-cards" css={reportingSectionStyle}>
                                                <Card>
                                                    <h3 className="v">${reportingData.aggregationData.revenue.toLocaleString()}</h3>
                                                    <div className={`info ${reportingData.aggregationData.revenue_change?.startsWith('-') ? 'down' : 'up'}`}>
                                                        <span>Net revenue</span>
                                                        <span>
                                                    <FontAwesomeIcon
                                                        fontSize={"1.5em"}
                                                        icon={faCaretUp}
                                                    />
                                                </span>
                                                        <span className="n">
                                                    {reportingData.aggregationData.revenue_change}
                                                </span>
                                                    </div>
                                                </Card>
                                                <Card>
                                                    <h3 className="v">${reportingData.aggregationData.ecpm.toLocaleString()}</h3>
                                                    <div className={`info ${reportingData.aggregationData.ecpm_change?.startsWith('-') ? 'down' : 'up'}`}>
                                                        <span>eCPM</span>
                                                        <span>
                                                    <FontAwesomeIcon
                                                        fontSize={"1.5em"}
                                                        icon={faCaretUp}
                                                    />
                                                </span>
                                                        <span className="n">
                                                    {reportingData.aggregationData.ecpm_change}
                                                </span>
                                                    </div>
                                                </Card>
                                                <Card>
                                                    <h3 className="v">{reportingData.aggregationData.impression.toLocaleString()}</h3>
                                                    <div className={`info ${reportingData.aggregationData.impression_change?.startsWith('-') ? 'down' : 'up'}`}>
                                                        <span>Impressions</span>
                                                        <span>
                                                    <FontAwesomeIcon
                                                        fontSize={"1.5em"}
                                                        icon={faCaretUp}
                                                    />
                                                </span>
                                                        <span className="n">
                                                    {reportingData.aggregationData.impression_change}
                                                </span>
                                                    </div>
                                                </Card>
                                                <Card>
                                                    <h3 className="v">{reportingData.aggregationData.winrates}%</h3>
                                                    <div className={`info ${reportingData.aggregationData.winrates_change?.startsWith('-') ? 'down' : 'up'}`}>
                                                        <span>Ad Server Win Rate</span>
                                                        <span>
                                                    <FontAwesomeIcon
                                                        fontSize={"1.5em"}
                                                        icon={faCaretUp}
                                                    />
                                                </span>
                                                        <span className="n">
                                                    {reportingData.aggregationData.winrates_change}
                                                </span>
                                                    </div>
                                                </Card>
                                            </section>)
                                            : null
                                    }

                                    <section css={reportingSectionStyle}>
                                        <Card>
                                            <div id="revenue-chart" ref={revenueChartRef}></div>
                                        </Card>
                                        <Card>
                                            <div id="win-rate-chart" ref={winRateChartRef}></div>
                                        </Card>
                                    </section>
                                </>
                            )
                        }

                    </section>
                </TabPanel>
                <TabPanel header="Report"/>
            </TabView>


        </LayoutWrapper>
    )
}
